import React, { useState, useRef, useEffect } from 'react';
import { Building2, User, Mail, Phone, Smartphone, FileEdit } from 'lucide-react';
import { useCustomStatesStore } from '../../lib/stores/customStatesStore';

interface ClientQuickInputProps {
  onSubmit: (clientData: QuickClientData) => Promise<void>;
  onCancel: () => void;
  onExpandForm?: (currentData: QuickClientData) => void;
  autoFocus?: boolean;
  initialName?: string;
}

export interface QuickClientData {
  business_name: string;
  entity_type: 'company' | 'individual';
  email?: string;
  phone?: string;
  whatsapp_number?: string;
  stato_cliente?: string;
}

export function ClientQuickInput({
  onSubmit,
  onCancel,
  onExpandForm,
  autoFocus = true,
  initialName = '',
}: ClientQuickInputProps) {
  const [formData, setFormData] = useState<QuickClientData>({
    business_name: initialName,
    entity_type: 'individual',
    email: '',
    phone: '',
    whatsapp_number: '',
    stato_cliente: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showContactError, setShowContactError] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Carica gli stati cliente per assegnare automaticamente il primo stato
  const { clientStates: storeClientStates, fetchClientStates } = useCustomStatesStore();

  useEffect(() => {
    // Carica gli stati solo se non sono già stati caricati
    if (!storeClientStates || storeClientStates.length === 0) {
      fetchClientStates();
    }
  }, [fetchClientStates, storeClientStates]);

  // Ottieni il primo stato cliente (non lead) per ordine
  const getFirstClientState = () => {
    if (!storeClientStates || storeClientStates.length === 0) return undefined;
    // Filtra solo gli stati cliente (is_lead = false) e prendi il primo per sort_order
    const clientOnlyStates = storeClientStates.filter(state => !state.is_lead && state.is_active);
    return clientOnlyStates.length > 0 ? clientOnlyStates[0].id : undefined;
  };

  useEffect(() => {
    if (autoFocus && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [autoFocus]);

  // Verifica se almeno un contatto è compilato
  const hasContact = !!(formData.email?.trim() || formData.phone?.trim() || formData.whatsapp_number?.trim());

  // Verifica se il form è valido per il submit
  const isFormValid = formData.business_name.trim() && hasContact;

  const handleSubmit = async () => {
    if (!formData.business_name.trim()) {
      return;
    }

    if (!hasContact) {
      setShowContactError(true);
      return;
    }

    setIsSubmitting(true);
    setShowContactError(false);
    try {
      // Assegna automaticamente il primo stato cliente (non lead)
      const dataToSubmit = {
        ...formData,
        stato_cliente: getFirstClientState(),
      };
      await onSubmit(dataToSubmit);
      // Reset form
      setFormData({
        business_name: '',
        entity_type: 'individual',
        email: '',
        phone: '',
        whatsapp_number: '',
        stato_cliente: '',
      });
      onCancel();
    } catch (error) {
      console.error('Error creating client:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleExpandClick = () => {
    if (onExpandForm) {
      onExpandForm(formData);
    }
  };

  // Nascondi l'errore quando l'utente inizia a compilare un contatto
  useEffect(() => {
    if (hasContact && showContactError) {
      setShowContactError(false);
    }
  }, [hasContact, showContactError]);

  return (
    <div>
      <div className="space-y-2">
        {/* Entity Type Selection */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, entity_type: 'company' }))}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
              formData.entity_type === 'company'
                ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:border-blue-500 dark:text-blue-400'
                : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-700/30 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <Building2 className="h-4 w-4" />
            <span>Azienda</span>
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, entity_type: 'individual' }))}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
              formData.entity_type === 'individual'
                ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:border-blue-500 dark:text-blue-400'
                : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-700/30 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            <User className="h-4 w-4" />
            <span>Persona fisica</span>
          </button>
        </div>

        {/* Business Name */}
        <div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {formData.entity_type === 'company' ? (
                <Building2 className="h-4 w-4 text-gray-400" />
              ) : (
                <User className="h-4 w-4 text-gray-400" />
              )}
            </div>
            <input
              ref={nameInputRef}
              type="text"
              value={formData.business_name}
              onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
              onKeyDown={handleKeyDown}
              placeholder={formData.entity_type === 'company' ? 'Nome azienda *' : 'Nome e cognome *'}
              className="w-full pl-10 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Nota campi obbligatori */}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          * Almeno un contatto è obbligatorio
        </p>

        {/* Email */}
        <div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              onKeyDown={handleKeyDown}
              placeholder="Email *"
              className="w-full pl-10 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Cellulare (WhatsApp) */}
        <div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Smartphone className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="tel"
              value={formData.whatsapp_number}
              onChange={(e) => setFormData(prev => ({ ...prev, whatsapp_number: e.target.value }))}
              onKeyDown={handleKeyDown}
              placeholder="Cellulare *"
              className="w-full pl-10 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Telefono */}
        <div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              onKeyDown={handleKeyDown}
              placeholder="Telefono"
              className="w-full pl-10 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100 placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Messaggio di errore se manca il contatto */}
        {showContactError && (
          <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
            <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
            Inserisci almeno un contatto (email, cellulare o telefono)
          </p>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onExpandForm && (
              <button
                type="button"
                onClick={handleExpandClick}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                <FileEdit className="h-3.5 w-3.5" />
                <span>Anagrafica completa</span>
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-700/30 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid}
              className="px-3 py-1 text-xs font-medium text-white bg-[#0f6cbd] hover:bg-[#0a5ba8] rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
